import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { venueApi } from '../lib/api';
import { geocodeVenue, getCityCoords, geocodeAddress, CITY_COORDS } from '../lib/geocoding';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  MapPin, Users, DollarSign, Search, Loader2, Building2,
  Wifi, Car, UtensilsCrossed, Music, Lightbulb, Star, Percent,
  CalendarDays, X, Map, Grid3X3, List
} from 'lucide-react';

// Leaflet imports for map
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icons in Leaflet with Webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const VENUE_TYPE_ICONS = {
  studio: Building2,
  event_space: Building2,
  outdoor: MapPin,
  restaurant: UtensilsCrossed,
  bar: UtensilsCrossed,
  theater: Building2,
  gallery: Building2,
  warehouse: Building2,
  rooftop: Building2,
  other: Building2,
};

// Use Mapbox geocoding for venue coordinates
const getVenueCoords = async (venue) => {
  return await geocodeVenue(venue);
};

// Moved outside to avoid nested component issues
const VenueIcon = ({ type }) => {
  const Icon = VENUE_TYPE_ICONS[type] || Building2;
  return <Icon className="h-5 w-5" />;
};

// Venue Card component - moved outside
const VenueCard = ({ venue }) => (
  <Card 
    className="border-border/50 overflow-hidden hover:border-primary/50 transition-colors group"
    data-testid={`venue-card-${venue.venue_id}`}
  >
    {/* Venue Image */}
    <div className="aspect-video bg-muted relative overflow-hidden">
      {venue.photos && venue.photos.length > 0 ? (
        <img 
          src={venue.photos[0]} 
          alt={venue.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Building2 className="h-12 w-12 text-muted-foreground" />
        </div>
      )}
      {venue.offers_discount && (
        <Badge className="absolute top-3 right-3 bg-green-500">
          <Percent className="h-3 w-3 mr-1" />
          {venue.discount_percent}% Off
        </Badge>
      )}
    </div>

    <CardContent className="p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-semibold text-lg line-clamp-1">{venue.name}</h3>
          <p className="text-sm text-muted-foreground flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            {venue.city || venue.address}
          </p>
        </div>
        <Badge variant="outline" className="shrink-0">
          <VenueIcon type={venue.venue_type} />
          <span className="ml-1 capitalize">{venue.venue_type?.replace('_', ' ')}</span>
        </Badge>
      </div>

      {venue.description && (
        <p className="text-sm text-muted-foreground line-clamp-2">
          {venue.description}
        </p>
      )}

      <div className="flex items-center gap-4 text-sm">
        {venue.capacity && (
          <span className="flex items-center gap-1">
            <Users className="h-4 w-4 text-muted-foreground" />
            {venue.capacity} guests
          </span>
        )}
        {venue.hourly_rate && (
          <span className="flex items-center gap-1 text-primary font-medium">
            <DollarSign className="h-4 w-4" />
            ${venue.hourly_rate}/hr
          </span>
        )}
      </div>

      {/* Amenities Preview */}
      {venue.amenities && venue.amenities.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {venue.amenities.slice(0, 4).map((amenity) => (
            <Badge key={amenity} variant="secondary" className="text-xs">
              {amenity}
            </Badge>
          ))}
          {venue.amenities.length > 4 && (
            <Badge variant="secondary" className="text-xs">
              +{venue.amenities.length - 4} more
            </Badge>
          )}
        </div>
      )}

      {venue.rating > 0 && (
        <div className="flex items-center gap-1 text-sm">
          <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
          <span className="font-medium">{venue.rating.toFixed(1)}</span>
          <span className="text-muted-foreground">({venue.review_count} reviews)</span>
        </div>
      )}

      <Link to={`/venues/${venue.venue_id}`}>
        <Button className="w-full rounded-xl mt-2" variant="outline">
          View Details
        </Button>
      </Link>
    </CardContent>
  </Card>
);

const Venues = () => {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metadata, setMetadata] = useState({ venue_types: [], amenities: [] });
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'list', 'map'
  const [venueCoords, setVenueCoords] = useState({}); // Cache for geocoded coordinates
  const [mapCenter, setMapCenter] = useState(CITY_COORDS.default); // Track geocoded map center
  const [filters, setFilters] = useState({
    search: '',
    city: '',
    venue_type: '',
    min_capacity: '',
    max_hourly_rate: '',
    has_discount: false,
    availability_date: null,
  });

  useEffect(() => {
    loadVenues();
    loadMetadata();
  }, []);
  
  // Geocode the search location when city filter changes
  useEffect(() => {
    const geocodeSearchLocation = async () => {
      if (filters.city && filters.city.trim()) {
        try {
          const coords = await geocodeAddress(filters.city);
          if (coords) {
            setMapCenter(coords);
          }
        } catch (error) {
          console.error('Failed to geocode search location:', error);
          // Fall back to city lookup
          setMapCenter(getCityCoords(filters.city));
        }
      } else {
        setMapCenter(CITY_COORDS.default);
      }
    };
    
    geocodeSearchLocation();
  }, [filters.city]);
  
  // Geocode venues when they change and map view is active
  useEffect(() => {
    if (viewMode === 'map' && venues.length > 0) {
      geocodeAllVenues();
    }
  }, [venues, viewMode]);
  
  const geocodeAllVenues = async () => {
    const newCoords = { ...venueCoords };
    let updated = false;
    
    for (const venue of venues) {
      if (!newCoords[venue.venue_id]) {
        try {
          const coords = await getVenueCoords(venue);
          newCoords[venue.venue_id] = coords;
          updated = true;
        } catch (error) {
          // Use fallback
          newCoords[venue.venue_id] = getCityCoords(venue.city || '');
        }
      }
    }
    
    if (updated) {
      setVenueCoords(newCoords);
    }
  };
  
  const getVenueCoordsSync = (venue) => {
    // Return cached coords or fallback to city-based
    return venueCoords[venue.venue_id] || getCityCoords(venue.city || '');
  };

  const loadMetadata = async () => {
    try {
      const response = await venueApi.getMetadata();
      setMetadata(response.data);
    } catch (error) {
      console.error('Failed to load venue metadata:', error);
    }
  };

  const loadVenues = async (searchFilters = {}) => {
    setLoading(true);
    try {
      const params = {};
      if (searchFilters.search) params.search = searchFilters.search;
      if (searchFilters.city) params.city = searchFilters.city;
      if (searchFilters.venue_type) params.venue_type = searchFilters.venue_type;
      if (searchFilters.min_capacity) params.min_capacity = parseInt(searchFilters.min_capacity);
      if (searchFilters.max_hourly_rate) params.max_hourly_rate = parseFloat(searchFilters.max_hourly_rate);
      if (searchFilters.has_discount) params.has_discount = true;
      if (searchFilters.availability_date) {
        params.availability_date = format(searchFilters.availability_date, 'yyyy-MM-dd');
      }
      
      const response = await venueApi.getAll(params);
      setVenues(response.data || []);
    } catch (error) {
      toast.error('Failed to load venues');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadVenues(filters);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      city: '',
      venue_type: '',
      min_capacity: '',
      max_hourly_rate: '',
      has_discount: false,
      availability_date: null,
    });
    loadVenues({});
  };

  // Get map center - use geocoded coordinates
  const getMapCenter = () => {
    return mapCenter;
  };

  return (
    <div className="min-h-screen pt-24 pb-12 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
            Browse Venues
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-4">
            Find the perfect space for your event, photoshoot, rehearsal, or production. 
            From intimate studios to grand event spaces—even party buses and party boats.
          </p>
          <Link to="/list-venue">
            <Button className="rounded-xl" data-testid="list-venue-header-btn">
              <Building2 className="h-4 w-4 mr-2" />
              List Your Venue
            </Button>
          </Link>
        </div>

        {/* Search & Filters */}
        <Card className="mb-8 border-border/50">
          <CardContent className="pt-6">
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="lg:col-span-2">
                <Input
                  placeholder="Search by venue name..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="rounded-xl"
                  data-testid="venue-name-search"
                />
              </div>
              <Input
                placeholder="City, State, or Zip..."
                value={filters.city}
                onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                className="rounded-xl"
                data-testid="venue-city-search"
              />
              <Select
                value={filters.venue_type || "all"}
                onValueChange={(value) => setFilters({ ...filters, venue_type: value === "all" ? "" : value })}
              >
                <SelectTrigger className="rounded-xl" data-testid="venue-type-filter">
                  <SelectValue placeholder="Venue Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {metadata.venue_types.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="number"
                placeholder="Max $/hr"
                value={filters.max_hourly_rate}
                onChange={(e) => setFilters({ ...filters, max_hourly_rate: e.target.value })}
                className="rounded-xl"
                data-testid="venue-price-filter"
              />
              <div className="flex gap-2">
                <Button 
                  onClick={handleSearch} 
                  className="flex-1 rounded-xl btn-glow"
                  data-testid="venue-search-btn"
                >
                  <Search className="h-4 w-4 mr-2" />
                  Search
                </Button>
                <Button 
                  onClick={handleClearFilters} 
                  variant="outline" 
                  className="rounded-xl"
                >
                  Clear
                </Button>
              </div>
            </div>
            
            {/* Date Picker & Quick Filters */}
            <div className="flex flex-wrap gap-2 mt-4 items-center justify-between">
              <div className="flex flex-wrap gap-2 items-center">
                {/* Availability Date Picker */}
                <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant={filters.availability_date ? "default" : "outline"}
                      className="rounded-full"
                      data-testid="venue-date-filter"
                    >
                      <CalendarDays className="h-4 w-4 mr-2" />
                      {filters.availability_date 
                        ? format(filters.availability_date, 'MMM d, yyyy')
                        : 'Available Date'
                      }
                      {filters.availability_date && (
                        <X
                          className="h-3 w-3 ml-2 hover:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilters({ ...filters, availability_date: null });
                          }}
                        />
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={filters.availability_date}
                      onSelect={(date) => {
                        setFilters({ ...filters, availability_date: date });
                        setDatePickerOpen(false);
                      }}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                <Button
                  variant={filters.has_discount ? "default" : "outline"}
                  size="sm"
                  className="rounded-full"
                  onClick={() => {
                    const newFilters = { ...filters, has_discount: !filters.has_discount };
                    setFilters(newFilters);
                    loadVenues(newFilters);
                  }}
                >
                  <Percent className="h-3 w-3 mr-1" />
                  With Discounts
                </Button>
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center gap-1 p-1 bg-muted rounded-lg">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  className="h-8 px-3 rounded-md"
                  onClick={() => setViewMode('grid')}
                  data-testid="view-grid-btn"
                >
                  <Grid3X3 className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  className="h-8 px-3 rounded-md"
                  onClick={() => setViewMode('list')}
                  data-testid="view-list-btn"
                >
                  <List className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'map' ? 'default' : 'ghost'}
                  size="sm"
                  className="h-8 px-3 rounded-md"
                  onClick={() => setViewMode('map')}
                  data-testid="view-map-btn"
                >
                  <Map className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : viewMode === 'map' ? (
          /* Map View - Show map even with no results */
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="border-border/50 overflow-hidden h-[600px]">
              <MapContainer
                center={getMapCenter()}
                zoom={filters.city ? 12 : 4}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {venues.map((venue) => {
                  const coords = getVenueCoordsSync(venue);
                  return (
                    <Marker key={venue.venue_id} position={coords}>
                      <Popup>
                        <div className="min-w-[200px]">
                          <h4 className="font-semibold">{venue.name}</h4>
                          <p className="text-sm text-muted-foreground">
                            {venue.city || venue.address}
                          </p>
                          {venue.hourly_rate && (
                            <p className="text-sm font-medium text-primary mt-1">
                              ${venue.hourly_rate}/hr
                            </p>
                          )}
                          <Link to={`/venues/${venue.venue_id}`}>
                            <Button size="sm" className="w-full mt-2 rounded-xl">
                              View Details
                            </Button>
                          </Link>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MapContainer>
            </Card>

            {/* Venue List alongside map */}
            <div className="space-y-4 max-h-[600px] overflow-y-auto">
              {venues.length === 0 ? (
                <Card className="border-border/50">
                  <CardContent className="py-12 text-center">
                    <Building2 className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                    <h3 className="font-medium mb-2">No Venues in This Area</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {filters.city 
                        ? `No venues found near "${filters.city}". Try a different location.`
                        : "Try searching for a specific city or browse available venues."
                      }
                    </p>
                    <Link to="/list-venue">
                      <Button className="rounded-xl" size="sm">List Your Venue</Button>
                    </Link>
                  </CardContent>
                </Card>
              ) : (
                venues.map((venue) => (
                  <Card 
                    key={venue.venue_id} 
                    className="border-border/50 hover:border-primary/50 transition-colors"
                  >
                    <CardContent className="p-4 flex gap-4">
                      {venue.photos && venue.photos.length > 0 ? (
                        <img 
                          src={venue.photos[0]} 
                          alt={venue.name}
                          className="w-24 h-24 object-cover rounded-lg shrink-0"
                        />
                      ) : (
                        <div className="w-24 h-24 bg-muted rounded-lg flex items-center justify-center shrink-0">
                          <Building2 className="h-8 w-8 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold line-clamp-1">{venue.name}</h3>
                        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                          <MapPin className="h-3 w-3" />
                          {venue.city || venue.address}
                        </p>
                        <div className="flex items-center gap-3 mt-2 text-sm">
                          {venue.capacity && (
                            <span className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              {venue.capacity}
                            </span>
                          )}
                          {venue.hourly_rate && (
                            <span className="text-primary font-medium">
                              ${venue.hourly_rate}/hr
                            </span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        ) : venues.length === 0 ? (
          /* Empty state for grid/list views */
          <Card className="border-border/50">
            <CardContent className="py-16 text-center">
              <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No Venues Found</h3>
              <p className="text-muted-foreground mb-4">
                {filters.city || filters.venue_type 
                  ? "Try adjusting your search filters or switch to Map view to explore the area"
                  : "No venues available in this area yet. Try a different search."
                }
              </p>
              <div className="flex gap-3 justify-center">
                <Button 
                  variant="outline" 
                  className="rounded-xl"
                  onClick={() => setViewMode('map')}
                >
                  <Map className="h-4 w-4 mr-2" />
                  View Map
                </Button>
                <Link to="/list-venue">
                  <Button className="rounded-xl">List Your Venue</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        ) : viewMode === 'list' ? (
          /* List View */
          <div className="space-y-4">
            {venues.map((venue) => (
              <Card 
                key={venue.venue_id} 
                className="border-border/50 hover:border-primary/50 transition-colors"
              >
                <CardContent className="p-4 flex gap-6">
                  {venue.photos && venue.photos.length > 0 ? (
                    <img 
                      src={venue.photos[0]} 
                      alt={venue.name}
                      className="w-48 h-32 object-cover rounded-lg shrink-0"
                    />
                  ) : (
                    <div className="w-48 h-32 bg-muted rounded-lg flex items-center justify-center shrink-0">
                      <Building2 className="h-12 w-12 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-semibold text-lg">{venue.name}</h3>
                        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                          <MapPin className="h-4 w-4" />
                          {venue.city || venue.address}
                        </p>
                      </div>
                      <Badge variant="outline">
                        <VenueIcon type={venue.venue_type} />
                        <span className="ml-1 capitalize">{venue.venue_type?.replace('_', ' ')}</span>
                      </Badge>
                    </div>
                    {venue.description && (
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                        {venue.description}
                      </p>
                    )}
                    <div className="flex items-center gap-6 mt-3">
                      {venue.capacity && (
                        <span className="flex items-center gap-1 text-sm">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          {venue.capacity} guests
                        </span>
                      )}
                      {venue.hourly_rate && (
                        <span className="text-primary font-medium">
                          ${venue.hourly_rate}/hr
                        </span>
                      )}
                      {venue.offers_discount && (
                        <Badge className="bg-green-500">
                          {venue.discount_percent}% Off
                        </Badge>
                      )}
                    </div>
                    {venue.amenities && venue.amenities.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {venue.amenities.slice(0, 6).map((amenity) => (
                          <Badge key={amenity} variant="secondary" className="text-xs">
                            {amenity}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col justify-center">
                    <Link to={`/venues/${venue.venue_id}`}>
                      <Button className="rounded-xl">View Details</Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          /* Grid View (default) */
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {venues.map((venue) => (
              <VenueCard key={venue.venue_id} venue={venue} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Venues;
